import {getNextPowerOfTwo, type ImageTilesDescriptor, RefCountableBase} from "@cm/utils"
import {Three as THREE} from "#three-texture-streaming/three-exports"
import {Box2, type Size2Like, type Vector2Like} from "@cm/math"
import type {AtlasFormat} from "#three-texture-streaming/texture-tile-constants"
import {
    TEXTURE_TILES_MAX_MIP_LEVELS,
    TEXTURE_TILES_MAX_TILES_X,
    TEXTURE_TILES_MAX_TILES_Y,
    TRACE_TILED_TEXTURES,
} from "#three-texture-streaming/texture-tile-constants"
import type {ComputeMipLevelTsl} from "#three-texture-streaming/shader-helpers"
import {getComputeMipLevelTsl} from "#three-texture-streaming/shader-helpers"
import {sceneLogger as logger} from "#three-texture-streaming/scene-logger"
import type {AtlasTileInfo} from "#three-texture-streaming/three-texture-tile-atlas"
import type {ThreeTiledTextureCallback} from "#three-texture-streaming/three-tiled-texture-callback"
import {TileFetchParamsHelper} from "#three-texture-streaming/tile-fetch-params-helper"

const TRACE = TRACE_TILED_TEXTURES

export class ThreeTiledTexture extends RefCountableBase {
    constructor(
        private callback: ThreeTiledTextureCallback,
        readonly id: number,
        readonly imageTilesDescriptor: ImageTilesDescriptor,
        readonly isSrgb: boolean,
    ) {
        super({
            disposeFn: () => this.dispose(),
        })

        if (this.imageTilesDescriptor.tileChannels === undefined) {
            logger.warn(`ThreeTiledTexture: tileChannels is undefined, defaulting to 4 (rgba8)`)
        }
        this.atlasFormat = this.imageTilesDescriptor.tileChannels === 1 ? "r8" : "rgba8"

        const mgr = this.callback.getAtlasManager()
        this._fine = new TileFetchParamsHelper(this.imageTilesDescriptor, mgr.tileSize, mgr.tileMargin)
        // mip1 tiles are half the content size with half the margin (66×66 px = floor(132/2))
        const mip1TileSize = {width: mgr.tileSize.width / 2, height: mgr.tileSize.height / 2}
        const mip1TileMargin = mgr.tileMargin / 2
        this._coarse = new TileFetchParamsHelper(this.imageTilesDescriptor, mip1TileSize, mip1TileMargin)

        if (this.numTiles.x > TEXTURE_TILES_MAX_TILES_X) {
            throw new Error(`ThreeTiledTexture: tileIndex numTilesX ${this.numTiles.x} exceeds supported max ${TEXTURE_TILES_MAX_TILES_X}`)
        }
        if (this.numTiles.y > TEXTURE_TILES_MAX_TILES_Y) {
            throw new Error(`ThreeTiledTexture: tileIndex numTilesY ${this.numTiles.y} exceeds supported max ${TEXTURE_TILES_MAX_TILES_Y}`)
        }
        this._bytesPerTileLookupEntry = 4

        const width = getNextPowerOfTwo(this.numTiles.x)
        const height = getNextPowerOfTwo(this.numTiles.y)
        const mipmaps = []
        let currentWidth = width
        let currentHeight = height
        while (currentWidth > 0 || currentHeight > 0) {
            mipmaps.push({
                data: null as unknown as Uint8Array,
                width: currentWidth,
                height: currentHeight,
            })
            if (currentWidth === 1 && currentHeight === 1) {
                break
            }
            currentWidth = Math.max(1, Math.floor(currentWidth / 2))
            currentHeight = Math.max(1, Math.floor(currentHeight / 2))
        }
        if (this.numMipLevels !== mipmaps.length) {
            throw new Error(`ThreeTiledTexture: tileIndex numMipLevels ${this.numMipLevels} does not match calculated mipmaps length ${mipmaps.length}`)
        }
        if (this.numMipLevels > TEXTURE_TILES_MAX_MIP_LEVELS) {
            throw new Error(`ThreeTiledTexture: tileIndex maxMipLevel ${this.numMipLevels} exceeds supported max ${TEXTURE_TILES_MAX_MIP_LEVELS}`)
        }

        this._tileLookupTexture = new THREE.DataTexture(null, width, height, THREE.RGBAFormat, THREE.UnsignedByteType)
        this._tileLookupTexture.generateMipmaps = false
        this._tileLookupTexture.mipmaps = mipmaps
        this._tileLookupTexture.minFilter = THREE.NearestMipmapNearestFilter
        this._tileLookupTexture.magFilter = THREE.NearestFilter
        this._tileLookupTexture.wrapS = THREE.ClampToEdgeWrapping
        this._tileLookupTexture.wrapT = THREE.ClampToEdgeWrapping
        this._tileLookupTexture.anisotropy = 1
        this._tileLookupTexture.colorSpace = THREE.NoColorSpace
        // source.dataReady = false: Three.js allocates the GPU texture (createTexture) but skips
        // the CPU-to-GPU upload (updateTexture) so we can avoid the allocating the backing arraybuffer
        this._tileLookupTexture.source.dataReady = false
        this._tileLookupTexture.needsUpdate = true

        for (let mipLevel = 0; mipLevel < this.numMipLevels; mipLevel++) {
            const mipmap = mipmaps[mipLevel]
            const data = new Uint8Array(mipmap.width * mipmap.height * this._bytesPerTileLookupEntry).fill(255) // default to invalid tile
            this._tileLookupDataByMipLevel.push({
                size: {width: mipmap.width, height: mipmap.height},
                data,
                invalidationBox: new Box2(),
            })
        }
    }

    protected dispose(): void {
        if (TRACE) {
            logger.log("ThreeTiledTexture: Disposing", this)
        }
        if (!this._tileLookupTexture) {
            throw new Error("ThreeTiledTexture: already disposed")
        }
        this.callback.disposeTiledTexture(this)
        this._tileLookupTexture = null
    }

    getComputeMipLevelTsl(): ComputeMipLevelTsl {
        const streamingMode = this.callback.getAtlasManager().streamingMode
        return getComputeMipLevelTsl(streamingMode)
    }

    getMaxPixelsPerTexel(): number {
        return this.callback.getAtlasManager().maxPixelsPerTexel
    }

    get numMipLevels(): number {
        return this._fine.numTileMipLevels
    }

    get numTiles(): Vector2Like {
        return this._fine.numTiles
    }

    get tileSize(): Size2Like {
        return this._fine.tileSize
    }

    get atlasTextureNode(): THREE.TextureNode {
        return this.callback.getAtlasManager().getAtlasTextureNode(this.atlasFormat)
    }

    get tileLookupTexture(): THREE.DataTexture {
        if (!this._tileLookupTexture) {
            throw new Error("ThreeTiledTexture: tileLookupTexture has been disposed")
        }
        return this._tileLookupTexture
    }

    getTileFetchParamsFine(mipLevel: number, tileIndex: Vector2Like) {
        return this._fine.getTileFetchParams(mipLevel, tileIndex)
    }

    getTileFetchParamsCoarse(mipLevel: number, tileIndex: Vector2Like) {
        return this._coarse.getTileFetchParams(mipLevel + 1, tileIndex)
    }

    setTileLookup(mipLevel: number, tileIndex: Vector2Like, atlasTileInfo: AtlasTileInfo | null) {
        const isEviction = atlasTileInfo === null
        if (!atlasTileInfo) {
            // if we are removing a tile, we need to find a coarser valid tile to fill in the gap
            const coarser = this.getCoarserTileLookupEntry(mipLevel, tileIndex)
            // mipShift=255 is the "no tile" sentinel; setTileLookupEntry propagates it to finer
            // entries that lack own data (prevMipShift !== 0), clearing stale fallback refs.
            const entry = coarser ?? {atlasTileInfo: {layer: 0, tileIndex: {x: 0, y: 0}}, mipShift: 255}
            this.setTileLookupEntry(mipLevel, tileIndex, entry, false, isEviction)
        } else {
            this.setTileLookupEntry(mipLevel, tileIndex, {atlasTileInfo, mipShift: 0}, false, isEviction)
        }
    }

    private getTileLookupEntry(mipLevel: number, tileIndex: Vector2Like): TileLookupEntry | null {
        const {size, data} = this._tileLookupDataByMipLevel[mipLevel]
        if (tileIndex.x < 0 || tileIndex.x >= size.width || tileIndex.y < 0 || tileIndex.y >= size.height) {
            throw new Error(`ThreeTiledTexture: getTileLookupEntry tileIndex out of bounds (${tileIndex.x},${tileIndex.y}) for mipLevel ${mipLevel}`)
        }
        const offset = (tileIndex.y * size.width + tileIndex.x) * this._bytesPerTileLookupEntry
        const mipShift = data[offset + 3]
        if (mipShift === 255) {
            return null
        }
        return {
            atlasTileInfo: {
                tileIndex: {
                    x: data[offset + 0],
                    y: data[offset + 1],
                },
                layer: data[offset + 2],
            },
            mipShift,
        }
    }

    private setTileLookupEntry(mipLevel: number, tileIndex: Vector2Like, entry: TileLookupEntry, isPropagated: boolean, isEviction: boolean): void {
        const {size, data, invalidationBox} = this._tileLookupDataByMipLevel[mipLevel]
        if (tileIndex.x < 0 || tileIndex.x >= size.width || tileIndex.y < 0 || tileIndex.y >= size.height) {
            if (isPropagated) {
                return
            }
            throw new Error(`ThreeTiledTexture: getTileLookupEntry tileIndex out of bounds (${tileIndex.x},${tileIndex.y}) for mipLevel ${mipLevel}`)
        }
        const offset = (tileIndex.y * size.width + tileIndex.x) * this._bytesPerTileLookupEntry
        const prevMipShift = data[offset + 3]
        if (isPropagated) {
            if (isEviction) {
                // eviction: replacement must reach stale refs — only protect own data
                if (prevMipShift === 0) return
            } else {
                // addition: don't overwrite entries that already have finer or equal fallbacks
                if (prevMipShift !== 255 && prevMipShift < entry.mipShift) return
            }
        }

        // set new data
        data[offset + 0] = entry.atlasTileInfo.tileIndex.x
        data[offset + 1] = entry.atlasTileInfo.tileIndex.y
        data[offset + 2] = entry.atlasTileInfo.layer
        data[offset + 3] = entry.mipShift

        // update invalidation box
        invalidationBox.expandByBoxInPlace({
            x: tileIndex.x,
            y: tileIndex.y,
            width: 1,
            height: 1,
        })
        this._needUpdateTileLookupTexture = true

        // propagate to finer levels
        if (mipLevel > 0) {
            const finerMipLevel = mipLevel - 1
            const finerTileIndexBase = {
                x: tileIndex.x * 2,
                y: tileIndex.y * 2,
            }
            const finerEntry: TileLookupEntry = {
                atlasTileInfo: entry.atlasTileInfo,
                mipShift: Math.min(entry.mipShift + 1, 255), // cap at 255 to preserve the "no tile" sentinel
            }
            for (let fy = 0; fy < 2; fy++) {
                for (let fx = 0; fx < 2; fx++) {
                    const finerTileIndex = {
                        x: finerTileIndexBase.x + fx,
                        y: finerTileIndexBase.y + fy,
                    }
                    this.setTileLookupEntry(finerMipLevel, finerTileIndex, finerEntry, true, isEviction)
                }
            }
        }
    }

    private getCoarserTileLookupEntry(mipLevel: number, tileIndex: Vector2Like): TileLookupEntry | null {
        let currentMipLevel = mipLevel
        let currentTileIndex = tileIndex
        while (currentMipLevel + 1 < this.numMipLevels) {
            currentMipLevel++
            currentTileIndex = {
                x: currentTileIndex.x >> 1,
                y: currentTileIndex.y >> 1,
            }
            const tileLookupEntry = this.getTileLookupEntry(currentMipLevel, currentTileIndex)
            if (tileLookupEntry) {
                return {
                    atlasTileInfo: tileLookupEntry.atlasTileInfo,
                    mipShift: currentMipLevel - mipLevel + tileLookupEntry.mipShift,
                }
            }
        }
        return null
    }

    flushTileLookupUpdates() {
        if (!this._needUpdateTileLookupTexture) {
            return
        }
        const renderer = this.callback.getAtlasManager().renderer

        for (let mipLevel = 0; mipLevel < this.numMipLevels; mipLevel++) {
            const mipLevelData = this._tileLookupDataByMipLevel[mipLevel]
            const {size, data, invalidationBox} = mipLevelData
            if (invalidationBox.isEmpty()) {
                continue
            }
            // Extract the dirty region into a contiguous buffer
            const dirtyWidth = invalidationBox.width
            const dirtyHeight = invalidationBox.height
            let srcData: Uint8Array
            if (dirtyWidth !== size.width) {
                // dirty region is not full-width, copy row-by-row into a contiguous buffer
                const tempData = new Uint8Array(dirtyWidth * dirtyHeight * this._bytesPerTileLookupEntry)
                for (let y = 0; y < dirtyHeight; y++) {
                    const srcOffset = ((y + invalidationBox.y) * size.width + invalidationBox.x) * this._bytesPerTileLookupEntry
                    const dstOffset = y * dirtyWidth * this._bytesPerTileLookupEntry
                    tempData.set(data.subarray(srcOffset, srcOffset + dirtyWidth * this._bytesPerTileLookupEntry), dstOffset)
                }
                srcData = tempData
            } else {
                if (invalidationBox.x !== 0) {
                    throw new Error("Unexpected invalidationBox.x != 0 for contiguous data")
                }
                // full-width dirty region, slice directly
                srcData = data.slice(
                    invalidationBox.y * size.width * this._bytesPerTileLookupEntry,
                    (invalidationBox.y + dirtyHeight) * size.width * this._bytesPerTileLookupEntry,
                )
            }

            // Upload dirty region via a temporary DataTexture, then GPU-copy it into the correct mip level
            const tempTex = new THREE.DataTexture(srcData, dirtyWidth, dirtyHeight, THREE.RGBAFormat, THREE.UnsignedByteType)
            tempTex.needsUpdate = true
            const dstPosition = new THREE.Vector2(invalidationBox.x, invalidationBox.y)

            // due to a bug in the WebGl fallback of copyTextureToTexture, we need to restore the framebuffer bindings after the copy
            const gl = (renderer.backend as any).gl ?? null
            const drawFramebuffer = gl?.getParameter(gl.DRAW_FRAMEBUFFER_BINDING) ?? null
            const readFramebuffer = gl?.getParameter(gl.READ_FRAMEBUFFER_BINDING) ?? null

            renderer.copyTextureToTexture(tempTex, this._tileLookupTexture!, null, dstPosition, 0, mipLevel)

            gl?.bindFramebuffer(gl.DRAW_FRAMEBUFFER, drawFramebuffer)
            gl?.bindFramebuffer(gl.READ_FRAMEBUFFER, readFramebuffer)

            tempTex.dispose()

            invalidationBox.makeEmpty()
        }
        this._needUpdateTileLookupTexture = false
    }

    readonly atlasFormat: AtlasFormat

    private _fine: TileFetchParamsHelper
    private _coarse: TileFetchParamsHelper
    private _bytesPerTileLookupEntry: number
    private _tileLookupTexture: THREE.DataTexture | null
    private _tileLookupDataByMipLevel = new Array<TileLookupMipLevelData>()
    private _needUpdateTileLookupTexture = false
}

type TileLookupMipLevelData = {
    size: Size2Like
    data: Uint8Array
    invalidationBox: Box2
}

type TileLookupEntry = {
    atlasTileInfo: AtlasTileInfo
    mipShift: number
}
